import { ChangeDetectionStrategy, Component, HostListener, computed, inject } from '@angular/core';
import { MotionOptions } from '@primeuix/motion';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { MediaLightboxItemContract } from '@shared/components/media/runtime/media-runtime.contract';
import { MediaLightboxService } from '@shared/services/media-lightbox.service';

const LIGHTBOX_DIALOG_MOTION_OPTIONS: MotionOptions = {
  duration: { enter: 1, leave: 1 }
};

const LIGHTBOX_MASK_MOTION_OPTIONS: MotionOptions = {
  duration: { enter: 1, leave: 1 }
};

@Component({
  selector: 'app-media-lightbox',
  standalone: true,
  imports: [ButtonModule, DialogModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './media-lightbox.component.html',
  styleUrls: ['./media-lightbox.component.scss'],
})
export class MediaLightboxComponent {
  readonly lightboxService = inject(MediaLightboxService);
  readonly dialogMotionOptions = LIGHTBOX_DIALOG_MOTION_OPTIONS;
  readonly maskMotionOptions = LIGHTBOX_MASK_MOTION_OPTIONS;

  readonly media = this.lightboxService.media;
  readonly visible = this.lightboxService.isVisible;
  readonly activeIndex = this.lightboxService.index;
  readonly shouldRender = this.lightboxService.shouldRender;

  readonly activeItem = computed<MediaLightboxItemContract | null>(() => {
    const items = this.media();
    if (items.length === 0) {
      return null;
    }
    return items[this.activeIndex()] ?? null;
  });

  readonly nextItem = computed<MediaLightboxItemContract | null>(() => {
    const items = this.media();
    if (items.length <= 1) {
      return null;
    }

    const nextIndex = (this.activeIndex() + 1) % items.length;
    return items[nextIndex] ?? null;
  });

  readonly prevItem = computed<MediaLightboxItemContract | null>(() => {
    const items = this.media();
    if (items.length <= 1) {
      return null;
    }

    const prevIndex = (this.activeIndex() - 1 + items.length) % items.length;
    return items[prevIndex] ?? null;
  });

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.visible()) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.prev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      default:
        break;
    }
  }

  onVisibleChange(nextVisible: boolean): void {
    if (!nextVisible) {
      this.close();
    }
  }

  onDialogHide(): void {
    this.lightboxService.finalizeClose();
  }

  onImageLoad(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLImageElement) {
      target.classList.add('loaded');
    }
  }

  setActiveIndex(index: number): void {
    this.lightboxService.setIndex(index);
  }

  trackMedia(index: number, item: MediaLightboxItemContract): string | number {
    return item.id ?? `${item.url}-${index}`;
  }

  prev(): void {
    this.lightboxService.previous();
  }

  next(): void {
    this.lightboxService.next();
  }

  close(): void {
    this.lightboxService.close();
  }
}
